import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Line, 
  Bar, 
  Doughnut, 
  Radar
} from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
  Alert,
  AlertTitle,
  Stack,
  CircularProgress,
  Tabs,
  Tab,
  Rating
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Speed,
  Timer,
  School,
  Psychology,
  EmojiEvents,
  Assignment,
  CheckCircle,
  Warning,
  Info,
  Star,
  TrackChanges,
  Analytics,
  Timeline,
  Insights,
  Lightbulb,
  TrendingFlat,
  AccessTime,
  Grade,
  Leaderboard,
  Schedule,
  Flag,
  Assessment,
  Book,
  PlayArrow,
  ExpandMore,
  Person,
  Groups,
  CalendarToday,
  Notifications,
  AutoGraph,
  PsychologyAlt,
  SchoolOutlined,
  WorkspacePremium,
  RocketLaunch,
  TrendingUpOutlined,
  SpeedOutlined,
  TimerOutlined,
  PsychologyOutlined
} from '@mui/icons-material';
import { useTheme } from '../ThemeContext';

// Import utilities
import { 
  useChartData, 
  createChartOptions, 
  createRadarOptions,
  getPerformanceColor, 
  getPerformanceLevel, 
  getPerformanceStars, 
  getPercentile,
  getTrendIcon,
  apiGet, 
  API_ENDPOINTS,
  LoadingSpinner, 
  ErrorDisplay, 
  EmptyState,
  PerformanceListItem,
  ChartContainer,
  ActionButton,
  MetricCard,
  createMetricCards,
  renderMetricCards
} from '../../utils';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const EnhancedDashboard = ({ user }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [timeRange, setTimeRange] = useState('week');
  const navigate = useNavigate();
  const { mode } = useTheme();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const result = await apiGet(API_ENDPOINTS.RESULTS.ENHANCED_DASHBOARD);
        if (result.success) {
          setDashboardData(result.data.data);
        } else {
          setError(result.error);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [navigate]);

  // Chart data using utilities
  const performanceTrendData = useChartData(dashboardData, mode, 'performanceTrend');
  const subjectPerformanceData = useChartData(dashboardData, mode, 'subjectPerformance');
  const scoreDistributionData = useChartData(dashboardData, mode, 'scoreDistribution');
  const radarData = useChartData(dashboardData, mode, 'radar');

  // Chart options using utilities
  const chartOptions = createChartOptions(mode);
  const radarOptions = createRadarOptions(mode);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  if (!dashboardData) {
    return (
      <EmptyState
        icon={<Assignment />}
        title="No Data Available"
        description="Start taking tests to see your performance analytics."
        action={
          <ActionButton
            component={Link}
            to="/tests"
            startIcon={<PlayArrow />}
            variant="contained"
          >
            Take Your First Test
          </ActionButton>
        }
      />
    );
  }

  const { overview = {}, recentPerformance = [], subjectPerformance = [], insights = [] } = dashboardData;

  // Generate metric cards using the utility
  const { primaryMetrics, secondaryMetrics } = createMetricCards(dashboardData, mode);

  return (
    <Container maxWidth={false} sx={{ 
      mt: { xs: 2, sm: 3, md: 4 }, 
      mb: { xs: 2, sm: 3, md: 4 }, 
      px: { xs: 1, sm: 2, md: 3, lg: 4 } 
    }}>
      {/* Header Section */}
      <Box mb={{ xs: 3, sm: 4, md: 5 }}>
        <Box 
          display="flex" 
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent="space-between" 
          alignItems={{ xs: 'flex-start', md: 'center' }} 
          gap={{ xs: 2, md: 0 }}
          mb={2}
        >
          <Box flex={1}>
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom 
              sx={{ 
              fontWeight: 'bold',
                fontSize: { xs: '1.75rem', sm: '2.125rem', md: '3rem' },
              background: mode === 'dark' ? 'linear-gradient(45deg, #90caf9, #42a5f5)' : 'linear-gradient(45deg, #1976d2, #42a5f5)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
              }}
            >
              Performance Analytics Hub
        </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              gutterBottom
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              Welcome back, {user?.name}! Your comprehensive performance insights are ready.
        </Typography>
          </Box>
          <Box display="flex" gap={{ xs: 1, sm: 2 }} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<CalendarToday />}
              onClick={() => setTimeRange(timeRange === 'week' ? 'month' : 'week')}
                size="medium"
                sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  px: { xs: 1, sm: 2 },
                  py: { xs: 0.5, sm: 1 }
                }}
            >
              {timeRange === 'week' ? 'This Week' : 'This Month'}
            </Button>
            <Button
              component={Link}
              to="/tests"
              variant="contained"
              startIcon={<PlayArrow />}
                size="medium"
              sx={{ 
                background: 'linear-gradient(45deg, #7cb342, #8bc34a)',
                  '&:hover': { background: 'linear-gradient(45deg, #689f38, #7cb342)' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  px: { xs: 1, sm: 2 },
                  py: { xs: 0.5, sm: 1 }
              }}
            >
              Take Test
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Metrics Section - Two rows of 4 cards each */}
      <Grid container spacing={{ xs: 2, sm: 3 }} mb={{ xs: 3, sm: 4 }}>
        {/* Primary Metrics Row */}
        {primaryMetrics.map((metric, index) => (
          <Grid item xs={12} sm={6} lg={3} key={`primary-${index}`}>
            <MetricCard
              title={metric.title}
              value={metric.value}
              subtitle={metric.subtitle}
              icon={metric.icon}
              color={metric.color}
              variant={metric.variant}
              mode={mode}
              trend={metric.trend}
              progress={metric.progress}
              target={metric.target}
              customContent={metric.customContent}
            />
        </Grid>
        ))}
        </Grid>

      <Grid container spacing={{ xs: 2, sm: 3 }} mb={{ xs: 3, sm: 4 }}>
        {/* Secondary Metrics Row */}
        {secondaryMetrics.map((metric, index) => (
          <Grid item xs={12} sm={6} lg={3} key={`secondary-${index}`}>
            <MetricCard
              title={metric.title}
              value={metric.value}
              subtitle={metric.subtitle}
              icon={metric.icon}
              color={metric.color}
              variant={metric.variant}
              mode={mode}
              trend={metric.trend}
              progress={metric.progress}
              target={metric.target}
              customContent={metric.customContent}
            />
        </Grid>
        ))}
      </Grid>

            {/* Analytics Tabs Section */}
      <Card elevation={4} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontWeight: 600,
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minWidth: { xs: 'auto', sm: '160px' }
            }
          }}
        >
          <Tab label="Performance Trends" icon={<Timeline />} iconPosition="start" />
          <Tab label="Subject Analysis" icon={<School />} iconPosition="start" />
          <Tab label="Time Analytics" icon={<Timer />} iconPosition="start" />
          <Tab label="Competition" icon={<Leaderboard />} iconPosition="start" />
        </Tabs>

        <Box p={{ xs: 2, sm: 3 }}>
          {activeTab === 0 && (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} lg={8}>
                <ChartContainer height={{ xs: 300, sm: 350, md: 400 }} noDataMessage="No trend data available">
                  {performanceTrendData && <Line data={performanceTrendData} options={chartOptions} />}
                </ChartContainer>
              </Grid>
        <Grid item xs={12} lg={4}>
                <ChartContainer height={{ xs: 300, sm: 350, md: 400 }} noDataMessage="No distribution data available">
                  {scoreDistributionData && <Doughnut data={scoreDistributionData} options={chartOptions} />}
                </ChartContainer>
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} lg={6}>
                <ChartContainer height={{ xs: 300, sm: 350, md: 400 }} noDataMessage="No subject data available">
                  {subjectPerformanceData && <Bar data={subjectPerformanceData} options={chartOptions} />}
                </ChartContainer>
              </Grid>
              <Grid item xs={12} lg={6}>
                <ChartContainer height={{ xs: 300, sm: 350, md: 400 }} noDataMessage="No radar data available">
                  {radarData && <Radar data={radarData} options={radarOptions} />}
                </ChartContainer>
              </Grid>
            </Grid>
          )}

          {activeTab === 2 && (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} lg={6}>
                <Card elevation={2}>
                  <CardHeader 
                    title="Time Management Analysis" 
                    avatar={<TimerOutlined />}
                    titleTypographyProps={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  />
                  <CardContent>
                    <Box 
                      display="flex" 
                      flexDirection={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-around" 
                      alignItems="center" 
                      gap={{ xs: 2, sm: 0 }}
                      mb={3}
                    >
                      <Box textAlign="center">
                        <Typography variant="h4" color="primary" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                          {(overview?.averageTimeEfficiency || 0).toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Time Efficiency
                        </Typography>
                      </Box>
                      <Box textAlign="center">
                        <Typography variant="h4" color="success.main" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                          {(overview?.averageSpeed || 0).toFixed(1)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Questions/Min
                        </Typography>
                      </Box>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={overview?.averageTimeEfficiency || 0}
                      color="primary"
                      sx={{ height: { xs: 6, sm: 8 }, borderRadius: 4 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Card elevation={2}>
                  <CardHeader 
                    title="Study Consistency" 
                    avatar={<Schedule />}
                    titleTypographyProps={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  />
            <CardContent>
                    <Box 
                      display="flex" 
                      flexDirection={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-around" 
                      alignItems="center" 
                      gap={{ xs: 2, sm: 0 }}
                      mb={3}
                    >
                      <Box textAlign="center">
                        <Typography variant="h4" color="success.main" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                          {overview?.activeDays || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Active Days
                        </Typography>
                      </Box>
                      <Box textAlign="center">
                        <Typography variant="h4" color="info.main" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                          {(overview?.testsPerDay || 0).toFixed(1)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Tests/Day
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Study streak: {overview?.activeDays || 0} consecutive days
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {activeTab === 3 && (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} lg={6}>
                <Card elevation={2}>
                  <CardHeader title="All India Ranking" avatar={<Leaderboard />} />
                  <CardContent>
                    <Box textAlign="center" py={{ xs: 2, sm: 3 }}>
                      <Typography variant="h2" color="primary" fontWeight="bold" mb={2} sx={{ fontSize: { xs: '2rem', sm: '3.75rem' } }}>
                        #{overview?.allIndiaRank || 'N/A'}
                      </Typography>
                      <Typography variant="h6" color="text.secondary" mb={3} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Out of {overview?.totalTestTakers || 0} students
                      </Typography>
                      <Chip 
                        label={`Top ${getPercentile(overview?.allIndiaRank, overview?.totalTestTakers)}%`}
                        color="success"
                        size="large"
                        sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' }, fontWeight: 'bold' }}
                      />
              </Box>
            </CardContent>
          </Card>
        </Grid>
              <Grid item xs={12} lg={6}>
                <Card elevation={2}>
                  <CardHeader title="Performance Comparison" avatar={<Groups />} />
                  <CardContent>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: 'success.main' }}>
                            <TrendingUp />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary="Above Average"
                          secondary={`${overview?.testsAbove70 || 0} tests scored above 70%`}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: 'warning.main' }}>
                            <EmojiEvents />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary="Excellent Performance"
                          secondary={`${overview?.testsAbove90 || 0} tests scored above 90%`}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
      </Grid>
            </Grid>
          )}
        </Box>
      </Card>

      {/* AI Insights & Quick Stats Section */}
      <Grid container spacing={{ xs: 2, sm: 3 }} mb={{ xs: 3, sm: 4 }}>
        <Grid item xs={12} lg={8}>
          <Card elevation={4} sx={{ height: '100%' }}>
            <CardHeader
              title="AI-Powered Insights"
              subheader="Personalized recommendations for improvement"
              avatar={<PsychologyAlt />}
              action={
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AutoGraph />}
                  sx={{ display: { xs: 'none', sm: 'flex' } }}
                >
                  Generate New Insights
                </Button>
              }
              titleTypographyProps={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              subheaderTypographyProps={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            />
            <CardContent>
              <Grid container spacing={{ xs: 1, sm: 2 }}>
                {insights.length > 0 ? (
                  insights.slice(0, 6).map((insight, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Alert
                        severity={
                          insight.type === 'strength' ? 'success' :
                          insight.type === 'weakness' ? 'warning' :
                          insight.type === 'improvement' ? 'info' : 'info'
                        }
                        icon={
                          insight.type === 'strength' ? <Star /> :
                          insight.type === 'weakness' ? <Warning /> :
                          insight.type === 'improvement' ? <TrackChanges /> : <Info />
                        }
                        sx={{ mb: 2 }}
                      >
                        <AlertTitle sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 'bold' }}>
                          {insight.title}
                        </AlertTitle>
                        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {insight.description}
                        </Typography>
                        {insight.confidence && (
                          <Box mt={1}>
                            <Typography variant="caption" color="text.secondary">
                              Confidence: {insight.confidence}%
                            </Typography>
                          </Box>
                        )}
                      </Alert>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Box textAlign="center" py={{ xs: 3, sm: 4 }}>
                      <PsychologyAlt sx={{ fontSize: { xs: 40, sm: 60 }, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        No Insights Available
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Take more tests to generate personalized insights
                      </Typography>
              </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card elevation={4} sx={{ height: '100%' }}>
            <CardHeader
              title="Quick Stats"
              subheader="Key performance indicators"
              avatar={<Assessment />}
              titleTypographyProps={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              subheaderTypographyProps={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            />
            <CardContent>
              <Stack spacing={{ xs: 2, sm: 3 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Tests Above 90%
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <LinearProgress
                      variant="determinate"
                      value={overview?.totalTests > 0 ? (overview.testsAbove90 / overview.totalTests) * 100 : 0}
                      color="success"
                      sx={{ flexGrow: 1, height: { xs: 6, sm: 8 }, borderRadius: 4 }}
                    />
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {overview?.testsAbove90 || 0}
                    </Typography>
              </Box>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Tests Above 80%
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <LinearProgress
                      variant="determinate"
                      value={overview?.totalTests > 0 ? (overview.testsAbove80 / overview.totalTests) * 100 : 0}
                      color="warning"
                      sx={{ flexGrow: 1, height: { xs: 6, sm: 8 }, borderRadius: 4 }}
                    />
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {overview?.testsAbove80 || 0}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Tests Above 70%
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <LinearProgress
                      variant="determinate"
                      value={overview?.totalTests > 0 ? (overview.testsAbove70 / overview.totalTests) * 100 : 0}
                      color="info"
                      sx={{ flexGrow: 1, height: { xs: 6, sm: 8 }, borderRadius: 4 }}
                    />
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {overview?.testsAbove70 || 0}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box textAlign="center">
                  <Typography variant="h4" color="primary" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {overview?.totalTests || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Total Tests Completed
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Performance & Study Plan Section */}
      <Grid container spacing={{ xs: 2, sm: 3 }} mb={{ xs: 3, sm: 4 }}>
        <Grid item xs={12} lg={8}>
          <Card elevation={4} sx={{ height: '100%' }}>
            <CardHeader
              title="Recent Test Performance"
              subheader="Your latest test results with detailed analysis"
              avatar={<Speed />}
              action={
                <Button
                  component={Link}
                  to="/tests"
                  variant="contained"
                  size="small"
                  startIcon={<PlayArrow />}
                  sx={{ 
                    background: 'linear-gradient(45deg, #7cb342, #8bc34a)',
                    '&:hover': { background: 'linear-gradient(45deg, #689f38, #7cb342)' },
                    display: { xs: 'none', sm: 'flex' }
                  }}
                >
                  Take Test
                </Button>
              }
              titleTypographyProps={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              subheaderTypographyProps={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            />
            <CardContent>
              {recentPerformance.length > 0 ? (
              <List>
                {recentPerformance.slice(0, 5).map((test, index) => (
                    <React.Fragment key={test.id || index}>
                      <PerformanceListItem
                        test={test}
                        mode={mode}
                        showDetails={true}
                      />
                      {index < Math.min(recentPerformance.length - 1, 4) && <Divider />}
                  </React.Fragment>
                ))}
              </List>
              ) : (
                <EmptyState
                  icon={<Assignment />}
                  title="No Recent Tests"
                  description="Start your learning journey by taking your first test"
                  action={
                    <ActionButton
                    component={Link}
                    to="/tests"
                    variant="contained"
                    startIcon={<PlayArrow />}
                    size="large"
                  >
                    Take Your First Test
                    </ActionButton>
                  }
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card elevation={4} sx={{ height: '100%' }}>
            <CardHeader
              title="Study Recommendations"
              subheader="AI-powered study suggestions"
              avatar={<SchoolOutlined />}
              titleTypographyProps={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              subheaderTypographyProps={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            />
            <CardContent>
              <Stack spacing={{ xs: 2, sm: 3 }}>
                <Box>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    Focus Areas
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {subjectPerformance && subjectPerformance.length > 0 ? (
                      subjectPerformance
                        .filter(subject => subject.accuracy < 70)
                        .slice(0, 3)
                        .map((subject, index) => (
                          <Chip
                    key={index}
                            label={`${subject.name}: ${subject.accuracy.toFixed(1)}%`}
                            color="warning"
                            variant="outlined"
                            size="small"
                          />
                        ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        No specific focus areas identified
                    </Typography>
                    )}
                  </Box>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="h6" gutterBottom color="success.main" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    Strengths
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {subjectPerformance && subjectPerformance.length > 0 ? (
                      subjectPerformance
                        .filter(subject => subject.accuracy >= 80)
                        .slice(0, 3)
                        .map((subject, index) => (
                          <Chip
                            key={index}
                            label={`${subject.name}: ${subject.accuracy.toFixed(1)}%`}
                            color="success"
                            variant="outlined"
                            size="small"
                          />
                        ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        Build your strengths by practicing more
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Divider />

                <Box textAlign="center">
                  <Button
                    component={Link}
                    to="/ai-assistant"
                    variant="outlined"
                    startIcon={<Psychology />}
                    fullWidth
                    size="medium"
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      py: { xs: 0.5, sm: 1 }
                    }}
                  >
                    Get AI Tutor Help
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions Section */}
      <Card elevation={4}>
          <CardContent>
          <Typography variant="h5" gutterBottom sx={{ 
            fontWeight: 'bold', 
            mb: { xs: 2, sm: 3 },
            fontSize: { xs: '1.25rem', sm: '1.5rem' }
          }}>
            Quick Actions & Tools
            </Typography>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <ActionButton
                  component={Link}
                  to="/tests"
                  variant="contained"
                startIcon={<PlayArrow />}
                  size="large"
                fullWidth
                                  sx={{ 
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  Take New Test
              </ActionButton>
              </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ActionButton
                  component={Link}
                  to="/performance"
                  variant="outlined"
                  startIcon={<Analytics />}
                  size="large"
                fullWidth
                sx={{ 
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
                >
                  Detailed Analytics
              </ActionButton>
              </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ActionButton
                  component={Link}
                  to="/ai-assistant"
                  variant="outlined"
                  startIcon={<Psychology />}
                  size="large"
                fullWidth
                sx={{ 
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
                >
                  AI Tutor
              </ActionButton>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ActionButton
                  component={Link}
                  to="/study-plan"
                  variant="outlined"
                  startIcon={<Flag />}
                  size="large"
                  fullWidth
                sx={{ 
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
                >
                  Study Plan
              </ActionButton>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
    </Container>
  );
};

export default EnhancedDashboard; 